import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import { FormikProps } from 'formik';
import { toast } from 'react-toastify';
import {
    BarcodeMismatchItem,
    checkBarcodeMismatches,
    CommonEcwComponentProps,
    EcwFormValues,
    findMatchedOption,
    getOrderKey,
    normalizeLotOrUnit,
    RouteFieldOptions,
    RouteFieldOptionsThinj,
    SelectUnitOption,
    SiteFieldOptions,
} from '../EcwConstants';
import useRecordUsage from '../../../Hooks/useRecordUsage';
import { Inventory, RecordUsageRes } from '../../../redux/recordUsage/recordUsageApiSlice';
import { Form, InputGroup, OverlayTrigger, Popover, Table } from 'react-bootstrap';
import BarcodeInfo from '../InfoComponents/BarcodeInfo';
import { useDispatch } from 'react-redux';
import {
    ecwOrderConfirmationStatus,
    ecwSelectedOrderInfo,
    setEcwSelectedBarcodeData,
} from '../../../redux/ecwOrder/ecwOrderSlice';
import ItemNameWarningDialog from '../InfoComponents/ItemNameWarningDialog';
import BarcodeMismatchWarningDialog from '../InfoComponents/BarcodeMismatchWarningDialog';
import { useAppSelector } from '../../../redux/hooks';

interface EcwUsageDetailsFormProps extends CommonEcwComponentProps {
    formik: FormikProps<EcwFormValues>;
    isSelectedOrderProcessed: boolean;
}

const EcwUsageDetailsForm: React.FC<EcwUsageDetailsFormProps> = ({ formik, setLoading, isSelectedOrderProcessed }) => {
    const dispatch = useDispatch();
    const barcodeTimeoutRef = useRef<NodeJS.Timeout>();
    const barcodeInputRef = useRef<HTMLInputElement>(null);
    const prevOrderKeyRef = useRef<string | number | null>(null);
    const barcodeMismatchesRef = useRef<BarcodeMismatchItem[]>([]);
    const selectedOrderInfo = useAppSelector(ecwSelectedOrderInfo);
    const confirmationStatus = useAppSelector(ecwOrderConfirmationStatus);

    const [barcodeData, setBarcodeData] = useState<RecordUsageRes | null>(null);
    const [useUnitsList, setUseUnitsList] = useState<SelectUnitOption[]>([]);
    const [isItemNameWarning, setIsItemNameWarning] = useState<boolean>(false);
    const [isImmunization, setIsImmunization] = useState<boolean>(false);
    const [isMismatchWarning, setIsMismatchWarning] = useState<boolean>(false);
    const [barcodeMismatches, setBarcodeMismatches] = useState<BarcodeMismatchItem[]>([]);

    const { getBarcodeDetailsAPI, getBarcodeDetailsLoading } = useRecordUsage();

    const { values, errors, touched, setFieldValue, handleChange, handleBlur, setFieldTouched, setFieldError, setSubmitting } = formik;

    const currentOrderKey = useMemo(() => {
        if (!values.orderId || !selectedOrderInfo) return null;
        return getOrderKey(selectedOrderInfo) ?? selectedOrderInfo.id ?? values.orderId ?? null;
    }, [values.orderId, selectedOrderInfo]);

    const thinjOrderValues = useMemo(() => {
        const medAdministration = selectedOrderInfo?.medicationAdministration;
        if (selectedOrderInfo?.orderType !== "THINJ" || !medAdministration?.fhirId) return null;

        const matchedRoute = findMatchedOption(medAdministration.route, RouteFieldOptionsThinj);
        const matchedSite = findMatchedOption(medAdministration.site, SiteFieldOptions);
        const dosageNum = medAdministration.dosage != null && !isNaN(Number(medAdministration.dosage))
            ? Number(medAdministration.dosage)
            : 0;

        return {
            route: matchedRoute?.value ?? "",
            routeLabel: matchedRoute?.value ? matchedRoute.label : undefined,
            site: matchedSite?.value ?? "",
            siteLabel: matchedSite?.value ? matchedSite.label : undefined,
            qty: dosageNum > 0 ? dosageNum : 0,
            unit: medAdministration.dosageUnit ?? "",
        };
    }, [selectedOrderInfo]);

    const latestSelectedOrderInfoRef = useRef(selectedOrderInfo);
    const latestConfirmationStatusRef = useRef(confirmationStatus);
    const latestThinjOrderValuesRef = useRef(thinjOrderValues);

    useEffect(() => {
        latestSelectedOrderInfoRef.current = selectedOrderInfo;
    }, [selectedOrderInfo]);

    useEffect(() => {
        latestConfirmationStatusRef.current = confirmationStatus;
    }, [confirmationStatus]);

    useEffect(() => {
        latestThinjOrderValuesRef.current = thinjOrderValues;
    }, [thinjOrderValues]);

    useEffect(() => {
        if (currentOrderKey !== prevOrderKeyRef.current) {
            prevOrderKeyRef.current = currentOrderKey;

            // Reset dialogs on order change
            setIsItemNameWarning(false);
            setIsMismatchWarning(false);
            barcodeMismatchesRef.current = [];
            setBarcodeMismatches([]);

            // If order changes and is not THINJ, ensure route, site, qty are reset
            if (selectedOrderInfo?.orderType !== "THINJ") {
                setFieldValue("route", formik.initialValues.route);
                setFieldValue("site", formik.initialValues.site);
                setFieldValue("qty", formik.initialValues.qty);
            }
        }
    }, [currentOrderKey, selectedOrderInfo?.orderType, formik.initialValues, setFieldValue]);

    // When order is confirmed and barcode was already scanned, apply order unit if matching
    useEffect(() => {
        if (confirmationStatus === "confirmed" && thinjOrderValues?.unit && useUnitsList.length > 0) {
            const matchedUnit = useUnitsList.find(
                u => normalizeLotOrUnit(u.value) === normalizeLotOrUnit(thinjOrderValues.unit)
            );
            if (matchedUnit) {
                setFieldValue("units", matchedUnit.value);
            }
        }
    }, [confirmationStatus, thinjOrderValues?.unit, useUnitsList, setFieldValue]);

    useEffect(() => {
        setLoading?.(getBarcodeDetailsLoading);
    }, [getBarcodeDetailsLoading, setLoading]);

    useEffect(() => {
        if (!values.barcode) {
            setBarcodeData(null);
            dispatch(setEcwSelectedBarcodeData(null));
            setUseUnitsList([]);
            setIsItemNameWarning(false);
            setIsMismatchWarning(false);
            barcodeMismatchesRef.current = [];
            setBarcodeMismatches([]);
        }
    }, [values.barcode, dispatch]);

    useEffect(() => {
        setFieldValue("itemInfo", {
            lotNumber: barcodeData?.lotNo ?? "",
            expiration: barcodeData?.expDate ?? "",
            ndcCode: barcodeData?.ndc ?? "",
            manufacturer: barcodeData?.manufacturer ?? "",
            cvXCode: barcodeData?.cvx ?? "",
            mvxCode: barcodeData?.mvxCode ?? "",
            mvxManufacturer: barcodeData?.mvxManufacturer ?? "",
            stock: barcodeData?.list2 ?? "",
            itemName: barcodeData?.rxName ?? "",
            itemType: barcodeData?.list1 ?? "",
        });
    }, [barcodeData, setFieldValue]);

    useEffect(() => {
        setIsImmunization(!!(selectedOrderInfo?.orderType === 'VAC' && selectedOrderInfo?.immId));
    }, [selectedOrderInfo]);

    const handleBarcodeBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        if (!e.target.value) {
            setFieldTouched("barcode", true, true);
            return;
        }

        const missingFields = [];
        if (!values.patID) missingFields.push('patID');
        if (!values.orderId) missingFields.push('orderId');

        if (missingFields.length) {
            missingFields.forEach(field => setFieldTouched(field, true));
            return;
        }

        // Clear previous timeout
        if (barcodeTimeoutRef.current) {
            clearTimeout(barcodeTimeoutRef.current);
        }

        // Debounce barcode API call
        barcodeTimeoutRef.current = setTimeout(async () => {
            try {
                const params = isImmunization ? { isImmunization: true } : {};
                const res: RecordUsageRes = await getBarcodeDetailsAPI({
                    ...values,
                    query: "true",
                }, params);

                if (res.status === "F") {
                    setFieldError("barcode", res.msg);
                    setFieldTouched("barcode", true, false);
                    setSubmitting(true);
                    return;
                }

                setSubmitting(false);

                // Use latest values via refs
                const latestOrder = latestSelectedOrderInfoRef.current;
                const latestThinj = latestThinjOrderValuesRef.current;
                const isConfirmed = latestConfirmationStatusRef.current === "confirmed";

                // 1. Item name warning check
                let hasItemNameMismatch = false;
                if (latestOrder?.medicationRequest) {
                    const orderItemName = latestOrder.medicationRequest?.medicationName?.trim()?.toLowerCase();
                    const barcodeItemName = res?.rxName?.trim()?.toLowerCase();

                    if (orderItemName && orderItemName !== barcodeItemName) {
                        hasItemNameMismatch = true;
                    }
                }

                // 2. Barcode vs order mismatch check (THINJ order)
                let mismatches: BarcodeMismatchItem[] = [];
                if (latestOrder?.orderType === "THINJ" && latestOrder?.medicationAdministration) {
                    mismatches = checkBarcodeMismatches(latestOrder.medicationAdministration, res);
                }

                barcodeMismatchesRef.current = mismatches;
                setBarcodeMismatches(mismatches);

                if (hasItemNameMismatch) {
                    setIsItemNameWarning(true);
                } else if (mismatches.length > 0) {
                    setIsMismatchWarning(true);
                }

                /* In API response we get unitName as unitCode, using unitName for value and label */
                const updatedUseUnitsList: SelectUnitOption[] = res?.useUnits?.map(({ unitName, isDefault }) => ({
                    value: unitName,
                    label: unitName,
                    isDefault: !!isDefault,
                })) ?? [];

                const defaultUnit = updatedUseUnitsList.find(u => u.isDefault) || updatedUseUnitsList[0] || null;
                const orderDosageUnit = (isConfirmed && latestThinj?.unit)
                    ? updatedUseUnitsList.find(u => normalizeLotOrUnit(u.value) === normalizeLotOrUnit(latestThinj.unit))
                    : undefined;

                setBarcodeData(res);
                dispatch(setEcwSelectedBarcodeData(res));
                setUseUnitsList(updatedUseUnitsList);
                setFieldValue("units", orderDosageUnit?.value || defaultUnit?.value || "");

                toast.success(res.msg);

            } catch (error) {
                console.error("Barcode lookup failed:", error);
            }
        }, 500);
    };

    const handleItemNameWarningAction = useCallback((action: "accept" | "cancel") => {
        setIsItemNameWarning(false);
        if (action === "accept") {
            if (barcodeMismatchesRef.current.length > 0) {
                setIsMismatchWarning(true);
            }
        } else {
            barcodeMismatchesRef.current = [];
            setBarcodeMismatches([]);
            setTimeout(() => {
                if (barcodeInputRef.current) {
                    barcodeInputRef.current.focus();
                    barcodeInputRef.current.select();
                }
            }, 0);
        }
    }, []);

    const handleMismatchWarningAction = useCallback((action: "accept" | "cancel") => {
        setIsMismatchWarning(false);
        if (action === "cancel") {
            barcodeMismatchesRef.current = [];
            setBarcodeMismatches([]);
            setTimeout(() => {
                if (barcodeInputRef.current) {
                    barcodeInputRef.current.focus();
                    barcodeInputRef.current.select();
                }
            }, 0);
        }
    }, []);

    const isOrderConfirmed = confirmationStatus === "confirmed";

    const lockedFields = useMemo(() => ({
        route: isOrderConfirmed && !isSelectedOrderProcessed && Boolean(thinjOrderValues?.route),
        site: isOrderConfirmed && !isSelectedOrderProcessed && Boolean(thinjOrderValues?.site),
        qty: isOrderConfirmed && !isSelectedOrderProcessed && Boolean(thinjOrderValues?.qty),
        units: isOrderConfirmed && !isSelectedOrderProcessed && Boolean(thinjOrderValues?.unit) && useUnitsList.some(u => normalizeLotOrUnit(u.value) === normalizeLotOrUnit(thinjOrderValues?.unit)),
    }), [isOrderConfirmed, isSelectedOrderProcessed, thinjOrderValues, useUnitsList]);

    const routeOptions = useMemo(() => {
        return (selectedOrderInfo?.orderType === "THINJ" || values.orderType === "THINJ")
            ? RouteFieldOptionsThinj
            : RouteFieldOptions;
    }, [selectedOrderInfo?.orderType, values.orderType]);

    const quantityPopover = useMemo(() => (
        <Popover id="quantityPopover">
            <Popover.Body>
                <Table hover={true}>
                    <thead>
                        <tr>
                            <th>Quantity</th>
                            <th>Unit</th>
                            <th>Location</th>
                        </tr>
                    </thead>
                    <tbody>
                        {barcodeData?.inventory && barcodeData.inventory.length > 0 ? (
                            barcodeData.inventory.map((item: Inventory, index: number) => (
                                <tr key={index}>
                                    <td>{item?.netQty}</td>
                                    <td>{barcodeData?.uom}</td>
                                    <td>{item?.locationName}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={3} style={{ textAlign: 'center', color: 'red' }}>
                                    No inventory found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </Popover.Body>
        </Popover>
    ), [barcodeData?.inventory, barcodeData?.uom]);

    return (
        <div className='item-usage-info-box'>
            <h4 className="form-section-title">Enter Item Usage Details</h4>
            <Form.Group className='form-field'>
                <Form.Label>Barcode*</Form.Label>
                <Form.Control
                    ref={barcodeInputRef}
                    type="text"
                    placeholder="Enter Barcode"
                    name="barcode"
                    value={values.barcode}
                    onChange={handleChange}
                    onBlur={handleBarcodeBlur}
                    isInvalid={touched.barcode && !!errors.barcode}
                    disabled={isSelectedOrderProcessed}
                />
                {errors.barcode && touched.barcode && (
                    <Form.Control.Feedback type="invalid">{errors.barcode}</Form.Control.Feedback>
                )}
            </Form.Group>
            <div className='form-field-wrapper'>
                <Form.Group className='form-field'>
                    <Form.Label>Quantity*</Form.Label>
                    <InputGroup>
                        <Form.Control
                            type="number"
                            placeholder="Enter Quantity"
                            name="qty"
                            value={values.qty}
                            onChange={handleChange}
                            onBlur={(e) => {
                                if (!isItemNameWarning && !isMismatchWarning) {
                                    handleBlur(e);
                                }
                            }}
                            isInvalid={touched.qty && !!errors.qty}
                            disabled={isSelectedOrderProcessed || lockedFields.qty}
                        />
                        {useUnitsList?.length > 0 && (
                            <Form.Select
                                aria-label="Select Quantity"
                                name="units"
                                className="unit-select"
                                value={values.units}
                                onChange={handleChange}
                                disabled={isSelectedOrderProcessed || lockedFields.units}
                                title={useUnitsList?.find(item => item.value === values.units)?.label || ""}
                            >
                                {useUnitsList.map((unit: SelectUnitOption, index: number) => (
                                    <option key={index} value={unit.value}>
                                        {unit.label}
                                    </option>
                                ))}
                            </Form.Select>
                        )}
                    </InputGroup>
                    {errors.qty && touched.qty && (
                        <Form.Control.Feedback type="invalid">{errors.qty}</Form.Control.Feedback>
                    )}
                    {barcodeData?.inventory && (
                        <OverlayTrigger trigger="hover" placement="bottom" overlay={quantityPopover}>
                            <Form.Text id="quantity-hint" muted>Inventory</Form.Text>
                        </OverlayTrigger>
                    )}
                </Form.Group>
                <Form.Group className='form-field'>
                    <Form.Label>Waste</Form.Label>
                    <Form.Control
                        type="number"
                        placeholder="Enter Waste"
                        name="waste"
                        value={values.waste}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.waste && !!errors.waste}
                        disabled={isSelectedOrderProcessed}
                    />
                    {errors.waste && touched.waste && (
                        <Form.Control.Feedback type="invalid">{errors.waste}</Form.Control.Feedback>
                    )}
                </Form.Group>
            </div>
            <Form.Group className='form-field'>
                <Form.Label>Route*</Form.Label>
                <Select
                    name='route'
                    options={routeOptions}
                    classNamePrefix="react-select"
                    placeholder="Please select route"
                    isDisabled={isSelectedOrderProcessed || lockedFields.route}
                    onChange={(selectedItem) => setFieldValue("route", selectedItem?.value)}
                    onBlur={() => setFieldTouched("route", true, true)}
                    value={routeOptions.find(item => item.value === values.route) || null}
                    className={`${touched.route && errors.route ? "is-invalid-border" : ""} react-select-container`}
                />
                {errors.route && touched.route && (
                    <Form.Control.Feedback type="invalid">{errors.route}</Form.Control.Feedback>
                )}
            </Form.Group>
            <Form.Group className='form-field'>
                <Form.Label>Site*</Form.Label>
                <Select
                    name='site'
                    options={SiteFieldOptions}
                    classNamePrefix="react-select"
                    placeholder="Please select site"
                    isDisabled={isSelectedOrderProcessed || lockedFields.site}
                    onChange={(selectedItem) => setFieldValue("site", selectedItem?.value)}
                    onBlur={() => setFieldTouched("site", true, true)}
                    value={SiteFieldOptions.find(item => item.value === values.site) || null}
                    className={`${touched.site && errors.site ? "is-invalid-border" : ""} react-select-container`}
                />
                {errors.site && touched.site && (
                    <Form.Control.Feedback type="invalid">{errors.site}</Form.Control.Feedback>
                )}
            </Form.Group>

            <BarcodeInfo barcodeInfo={barcodeData} />

            {isItemNameWarning && (
                <ItemNameWarningDialog
                    show={isItemNameWarning}
                    onAccept={() => handleItemNameWarningAction("accept")}
                    onCancel={() => handleItemNameWarningAction("cancel")}
                    orderItemName={selectedOrderInfo?.medicationRequest?.medicationName ?? ""}
                    barcodeItemName={barcodeData?.rxName ?? ""}
                />
            )}

            {isMismatchWarning && (
                <BarcodeMismatchWarningDialog
                    show={isMismatchWarning}
                    mismatches={barcodeMismatches}
                    onAccept={() => handleMismatchWarningAction("accept")}
                    onCancel={() => handleMismatchWarningAction("cancel")}
                />
            )}
        </div>
    );
};

export default EcwUsageDetailsForm;
